import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  Typography,
  Box
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import axios from 'axios';

interface ExecutionRecord {
  executionTime: string;
  status: 'success' | 'failed';
  messagesSent: number;
  errorMessage?: string;
}

interface ExecutionHistoryProps {
  messageId: string;
  open: boolean;
  onClose: () => void;
}

const ExecutionHistory: React.FC<ExecutionHistoryProps> = ({ messageId, open, onClose }) => {
  const [history, setHistory] = useState<ExecutionRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open && messageId) {
      loadHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, messageId]);

  const loadHistory = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(`/api/scheduled-messages/${messageId}/history`);
      setHistory(response.data.history || []);
    } catch (err: any) {
      console.error('Error loading execution history:', err);
      setError(err.response?.data?.error || 'Failed to load execution history');
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString([], {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Execution History</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : history.length === 0 ? (
          <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
            No execution history yet
          </Typography>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Execution Time</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Messages Sent</TableCell>
                  <TableCell>Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((record, index) => (
                  <TableRow key={index}>
                    <TableCell>{formatTimestamp(record.executionTime)}</TableCell>
                    <TableCell>
                      <Chip
                        icon={record.status === 'success' ? <CheckCircleIcon /> : <ErrorIcon />}
                        label={record.status}
                        color={record.status === 'success' ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{record.messagesSent}</TableCell>
                    <TableCell>
                      {record.errorMessage ? (
                        <Typography variant="body2" color="error">
                          {record.errorMessage}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExecutionHistory;
